// Compliance Score Calculation Utility
// F-015: Strict Compliance Logic & Litigation Health Score
// A property is ONLY "Compliant" if:
// 1. All 3 critical documents (Gas Safety, EPC, EICR) are present AND valid
// 2. Score = 100%

import { getAppMarket } from '../services/MarketService';

export interface HealthItem {
    type: string;
    status: 'VALID' | 'INVALID' | 'WARN' | string;
    description: string;
    score?: number;
}

export interface HealthPillar {
    score: number;
    max: number;
    status: 'PASS' | 'WARN' | 'FAIL' | string;
    items: HealthItem[];
}

export interface HealthData {
    totalScore: number;
    status: string;
    pillars: {
        criticalDocs: HealthPillar;
        syntaxWording: HealthPillar;
        investigatory: HealthPillar;
        adminHygiene: HealthPillar;
    };
}

export interface Certificate {
    id: string;
    name?: string;
    type: string;
    // Backend stores expiry under EITHER `expires` OR `expiryDate` depending
    // on the upload path. Read both when computing the score.
    expires?: string;
    expiryDate?: string;
    status?: string;
    s3Key?: string;
}

/**
 * Parse a date string that might be ISO (YYYY-MM-DD), native (Date parseable),
 * OR UK-style DD/MM/YYYY (with `/`, `-`, or `.` separators). The bulk-import
 * flow stores expiries as UK strings like "13/05/2030" — `new Date(...)`
 * on that returns Invalid Date, so we handle it explicitly.
 * Returns a valid Date or null.
 */
export const parseFlexibleDate = (raw: unknown): Date | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
    const s = String(raw).trim();
    if (!s) return null;

    // 1. Native parse (ISO 8601, RFC 2822, etc.)
    const native = new Date(s);
    if (!isNaN(native.getTime())) return native;

    // 2. UK-style DD/MM/YYYY (also DD-MM-YYYY and DD.MM.YYYY, 2-digit year OK)
    const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2}|\d{4})$/);
    if (m) {
        let [, dd, mm, yyyy] = m;
        if (yyyy.length === 2) yyyy = (parseInt(yyyy, 10) > 50 ? '19' : '20') + yyyy;
        const iso = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:00:00Z`;
        const d = new Date(iso);
        if (!isNaN(d.getTime())) return d;
    }

    return null;
};

// The 3 critical certificate types required for compliance
// Added aliases for robustness (matching backend health.js)
const CRITICAL_CERTIFICATE_DEFINITIONS = [
    { type: 'gas_safety', aliases: ['gas', 'cp12', 'gassafety'] },
    { type: 'epc', aliases: ['energy', 'epc'] },
    { type: 'eicr', aliases: ['electrical', 'eicr'] }
];

export const CRITICAL_CERTIFICATE_TYPES = CRITICAL_CERTIFICATE_DEFINITIONS.map(d => d.type);

/**
 * Calculate compliance score based on REQUIRED critical certificates
 * @param certificates Array of certificates for a property
 * @returns Score from 0-100 (based on 3 critical pillars)
 */
export function calculateComplianceScore(certificates: Certificate[], hasGasInstallation: boolean = true): number {
    if (!certificates) {
        return 0;
    }

    const { validCriticalCount } = checkCriticalCertificates(certificates, hasGasInstallation);

    // Score is strictly based on the 3 critical documents
    // 3/3 = 100%, 2/3 = 66%, 1/3 = 33%, 0/3 = 0%
    return Math.round((validCriticalCount / 3) * 100);
}

/**
 * Check if all critical certificates are present and valid
 * @param certificates Array of certificates for a property
 * @returns Object with hasCritical boolean and missing types
 */
export function checkCriticalCertificates(certificates: Certificate[], hasGasInstallation: boolean = true): {
    hasAllCritical: boolean;
    missingTypes: string[];
    validCriticalCount: number;
} {
    if (!certificates || certificates.length === 0) {
        return {
            hasAllCritical: false,
            missingTypes: CRITICAL_CERTIFICATE_TYPES,
            validCriticalCount: 0
        };
    }

    const now = new Date();
    const validCriticalTypes: string[] = [];
    const missingTypes: string[] = [];

    CRITICAL_CERTIFICATE_DEFINITIONS.forEach(def => {
        // SKIP Gas Safety if property doesn't have gas installation
        if (def.type === 'gas_safety' && hasGasInstallation === false) {
            validCriticalTypes.push(def.type); // Consider "passed" if not applicable
            return;
        }

        // Find ALL certificates matching this definition (type or alias), then
        // pick the newest one by createdAt / uploadTimestamp. Users can end up
        // with duplicates when Replace uploads a new cert without removing the
        // old one — the freshest expiry should always win, not the first-listed
        // Dynamo item.
        const matches = certificates.filter(c => {
            const cType = (c.type || '').toLowerCase().replace(/[\s_-]/g, '');
            const cName = (c['name'] || '').toLowerCase();
            if (cType.includes(def.type.replace(/_/g, ''))) return true;
            return def.aliases.some(alias => cType.includes(alias) || cName.includes(alias));
        });
        const certAge = (c: any) => {
            const t = c?.uploadTimestamp || c?.createdAt || c?.expires || c?.expiryDate || 0;
            const d = new Date(t).getTime();
            return isNaN(d) ? 0 : d;
        };
        const cert = matches.length
            ? matches.slice().sort((a, b) => certAge(b) - certAge(a))[0]
            : undefined;

        // Robust expiry resolution: try every candidate field on the cert
        // (backend has stored expiry under `expires`, `expiryDate`, `expiry`,
        // and `expiration` at various points). Pick the FIRST one that parses
        // to a real date — an invalid/garbage string in `expires` shouldn't
        // shadow a good value in `expiryDate`.
        const expiryCandidates = cert
            ? [cert.expires, cert.expiryDate, (cert as any).expiry, (cert as any).expiration]
            : [];
        let validExpiry: Date | null = null;
        for (const raw of expiryCandidates) {
            const d = parseFlexibleDate(raw);
            if (d) { validExpiry = d; break; }
        }

        if (cert && validExpiry) {
            if (validExpiry > now) {
                validCriticalTypes.push(def.type);
            } else {
                missingTypes.push(def.type); // Expired
            }
        } else {
            missingTypes.push(def.type); // Missing or unparseable expiry
        }
    });

    return {
        hasAllCritical: validCriticalTypes.length === CRITICAL_CERTIFICATE_DEFINITIONS.length,
        missingTypes,
        validCriticalCount: validCriticalTypes.length
    };
}

/**
 * F-015: Calculate Litigation Health Score (0-100%)
 * Incorporates 4 Pillars: Critical Docs, Syntax/Wording, Investigatory, Admin Hygiene
 */
const PRS_GO_LIVE = new Date('2025-10-01');

export function calculateHealthScore(
    certificates: Certificate[],
    analysisResults?: any,
    prsRegistration?: { prsRegistrationNumber?: string; prsExpiryDate?: string }
): HealthData {
    // ---------------------------------------------------------
    // Pillar 1: Critical Documents (50 points max - 50%)
    // ---------------------------------------------------------
    const hasGasInstallation = (certificates as any).hasGasInstallation !== false; // Fallback if not injected or set on array
    const criticalCheck = checkCriticalCertificates(certificates || [], hasGasInstallation);
    const p1Score = Math.round((criticalCheck.validCriticalCount / 3) * 50);

    const p1Items: HealthItem[] = CRITICAL_CERTIFICATE_TYPES.map(type => {
        const isMissing = criticalCheck.missingTypes.includes(type);
        return {
            type: type.toUpperCase().replace('_', ' '),
            status: isMissing ? 'INVALID' : 'VALID',
            description: isMissing ? 'Missing or Expired' : 'Valid'
        };
    });

    const p1: HealthPillar = {
        score: p1Score,
        max: 50,
        status: p1Score === 50 ? 'PASS' : 'FAIL',
        items: p1Items
    };

    // ---------------------------------------------------------
    // Pillar 2: Syntax & Wording (20 points max - 20%)
    // ---------------------------------------------------------
    // Check if Tenancy Agreement exists. 
    const tenancyAgreement = certificates?.find(c =>
        (c.type || '').toLowerCase().includes('tenancy') || (c.type || '').toLowerCase().includes('agreement')
    );

    let p2Score = 0;
    let p2Status: 'PASS' | 'WARN' | 'FAIL' = 'FAIL';
    let p2Items: HealthItem[] = [];

    if (tenancyAgreement) {
        // Agreement exists.
        //
        // Resolution order:
        //   1. `analysisResults` arg — kept for callers that already have a
        //      fresh NLP payload in hand.
        //   2. `cert.nlpStatus` on the tenancy-agreement record itself —
        //      the analyzer writes this on upload, so we should reflect it
        //      instead of falsely showing "analysis pending". Any status
        //      other than 'pending' / null / '' counts as complete for
        //      pillar-2 purposes.
        const nlpStatus = String((tenancyAgreement as any).nlpStatus || '').toLowerCase();
        const isAnalyzed = !!analysisResults || (!!nlpStatus && nlpStatus !== 'pending' && nlpStatus !== 'queued');

        if (analysisResults) {
            // Use real NLP results if available
            p2Score = analysisResults.score || 20;
            p2Status = p2Score === 20 ? 'PASS' : 'WARN';
            p2Items.push({
                type: 'NLP Analysis',
                status: p2Status === 'PASS' ? 'VALID' : 'WARN',
                description: analysisResults.summary || 'Analysis Complete'
            });
        } else if (isAnalyzed) {
            // Cert itself carries a completed NLP status — treat as full pass.
            p2Score = 20;
            p2Status = 'PASS';
            p2Items.push({
                type: 'Tenancy Agreement',
                status: 'VALID',
                description: 'Document present'
            });
            p2Items.push({
                type: 'Syntax Check',
                status: 'VALID',
                description: 'Analysis complete'
            });
        } else {
            // Agreement exists but not analyzed - give partial score
            p2Score = 16;
            p2Status = 'WARN';
            p2Items.push({
                type: 'Tenancy Agreement',
                status: 'VALID',
                description: 'Document present'
            });
            p2Items.push({
                type: 'Syntax Check',
                status: 'WARN',
                description: 'Analysis pending'
            });
        }
    } else {
        // No tenancy agreement - show as N/A, not a warning
        p2Score = 0;
        p2Status = 'FAIL';
        p2Items.push({
            type: 'Tenancy Agreement',
            status: 'INVALID',
            description: 'No document uploaded'
        });
    }

    const p2: HealthPillar = {
        score: p2Score,
        max: 20,
        status: p2Status,
        items: p2Items
    };

    // ---------------------------------------------------------
    // Pillar 3: Investigatory Readiness (20 points max - 20%)
    // ---------------------------------------------------------
    const now = new Date();
    const isTexasMarket = getAppMarket() === 'texas';
    // The PRS go-live date is a UK milestone. Municipal rental registration in
    // Texas is not gated by it, so the zero-out below never applies there.
    const isAfterPrsGoLive = !isTexasMarket && now >= PRS_GO_LIVE;
    const hasPrsReg = !!(prsRegistration?.prsRegistrationNumber);

    let p3Score = p1Score > 0 ? 16 : 0;
    if (isAfterPrsGoLive && !hasPrsReg) {
        p3Score = 0;
    }

    let p3Status: string;
    if (p3Score >= 16) p3Status = 'PASS';
    else if (p3Score > 0) p3Status = 'WARN';
    else p3Status = 'FAIL';

    let prsItemStatus: string;
    let prsItemDescription: string;
    // F-307: PRS is UK terminology. Texas uses municipal rental registration
    // (no fixed national go-live date), so the copy and the item label swap.
    if (isAfterPrsGoLive && !hasPrsReg) {
        prsItemStatus = isTexasMarket ? 'WARN' : 'INVALID';
        prsItemDescription = isTexasMarket
            ? 'No municipal registration on file'
            : 'Not registered — required since Oct 2025';
    } else if (hasPrsReg) {
        prsItemStatus = 'VALID';
        prsItemDescription = `Registered: ${prsRegistration!.prsRegistrationNumber}`;
    } else {
        prsItemStatus = 'WARN';
        prsItemDescription = isTexasMarket
            ? 'Municipal registration not yet recorded'
            : 'Registration optional until Oct 2025';
    }

    const p3: HealthPillar = {
        score: p3Score,
        max: 20,
        status: p3Status,
        items: [
            { type: 'Digital Retrieve', status: p3Score > 8 ? 'VALID' : 'WARN', description: 'Docs accessible' },
            { type: 'Audit Trail', status: 'WARN', description: 'Partial records' },
            { type: isTexasMarket ? 'Rental Registration' : 'PRS Registration', status: prsItemStatus, description: prsItemDescription }
        ]
    };

    // ---------------------------------------------------------
    // Pillar 4: Admin Hygiene (10 points max - 10%)
    // ---------------------------------------------------------
    const p4Score = 7;
    const p4: HealthPillar = {
        score: p4Score,
        max: 10,
        status: 'WARN',
        items: [
            { type: 'Comms Log', status: 'WARN', description: 'No logs found' },
            { type: 'Rent Receipts', status: 'WARN', description: 'Manual check needed' }
        ]
    };

    // ---------------------------------------------------------
    // Total Calculation
    // ---------------------------------------------------------
    const totalScore = p1Score + p2Score + p3Score + p4Score;

    let status = 'High Risk';
    if (totalScore >= 90) status = 'Litigation Ready';
    else if (totalScore >= 70) status = 'Good Health';
    else if (totalScore >= 50) status = 'Moderate Risk';

    return {
        totalScore,
        status,
        pillars: {
            criticalDocs: p1,
            syntaxWording: p2,
            investigatory: p3,
            adminHygiene: p4
        }
    };
}

/**
 * Get compliance status based on score AND critical certificate check
 * STRICT LOGIC: Only "Compliant" if ALL 3 critical docs are valid AND score is 100%
 * @param score Compliance score (0-100)
 * @param certificates Array of certificates for critical doc check
 * @returns Status object with text and color
 */
export function getComplianceStatus(
    score: number,
    certificates?: Certificate[]
): {
    text: string;
    color: string;
    isCompliant: boolean;
    reason?: string;
} {
    // If certificates provided, do strict check
    if (certificates) {
        const hasGasInstallation = (certificates as any).hasGasInstallation !== false;
        const criticalCheck = checkCriticalCertificates(certificates, hasGasInstallation);

        // STRICT: Must have ALL 3 critical docs AND score = 100
        if (criticalCheck.hasAllCritical && score === 100) {
            return {
                text: 'COMPLIANT',
                color: 'green',
                isCompliant: true
            };
        }

        // Not compliant - determine reason
        if (!criticalCheck.hasAllCritical) {
            return {
                text: 'ACTION REQUIRED',
                color: 'red',
                isCompliant: false,
                reason: `Missing: ${criticalCheck.missingTypes.join(', ').toUpperCase()}`
            };
        }

        // Has all critical but score < 100 (some non-critical expired)
        return {
            text: 'ACTION REQUIRED',
            color: 'amber',
            isCompliant: false,
            reason: 'Some certificates expired'
        };
    }

    // Fallback: score-only check (legacy behavior)
    const isCompliant = score === 100;
    return {
        text: isCompliant ? 'COMPLIANT' : 'ACTION REQUIRED',
        color: isCompliant ? 'green' : 'red',
        isCompliant
    };
}
